import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
    getApiRecord,
    getActiveXeroConfiguration,
    getCustomersForDropdown,
    getAccountCodesForDropdown,
    getXeroConfigurations,
    getMaestroInvoices,
} from 'services/XeroService'
import { getApiData as getCompaniesData } from 'services/CompanyService'

export const getData = createAsyncThunk('xero/data/getData', async data => {
    // Use getMaestroInvoices instead of getApiData to only show Maestro-created draft invoices
    const response = await getMaestroInvoices(data)
    return response.data
})

export const getRecord = createAsyncThunk('xero/data/getRecord', async id => {
    const response = await getApiRecord(id)
    return response.data
})

export const getCompanies = createAsyncThunk('xero/data/getCompanies', async () => {
    try {
        console.log('Fetching companies using CompanyService...')
        const response = await getCompaniesData({ pageIndex: 1, pageSize: 100 })
        console.log('Companies API response:', response)
        console.log('Companies data:', response.data)
        return response.data?.results || []
    } catch (error) {
        console.error('Error fetching companies:', error)
        throw error
    }
})

export const getXeroConfigsForCompany = createAsyncThunk(
    'xero/data/getXeroConfigsForCompany',
    async ({ companyId = null, configId = null } = {}) => {
        console.log('getXeroConfigsForCompany called with:', { companyId, configId })
        try {
            let params = { is_active: true }

            if (companyId) {
                params.company = companyId
            }

            if (configId) {
                params.id = configId
            }

            const response = await getXeroConfigurations(params)
            console.log('getXeroConfigurations response:', response.data)
            return response.data?.results || []
        } catch (error) {
            console.error('Error fetching Xero configurations:', error)
            throw error
        }
    }
)

export const getXeroConfigs = createAsyncThunk('xero/data/getXeroConfigs', async () => {
    const response = await getXeroConfigurations({ is_active: true })
    return response.data?.results || []
})

export const getXeroConfig = createAsyncThunk('xero/data/getXeroConfig', async () => {
    const response = await getActiveXeroConfiguration()
    return response.data?.results?.[0] || null
})

export const getCustomers = createAsyncThunk(
    'xero/data/getCustomers',
    async ({ configurationId = null, companyId = null } = {}) => {
        console.log('getCustomers thunk called with:', { configurationId, companyId })
        try {
            const customers = await getCustomersForDropdown(configurationId, companyId)
            console.log('getCustomers thunk result:', customers)
            return customers
        } catch (error) {
            console.error('Error fetching customers:', error)
            throw error
        }
    }
)

export const getAccountCodes = createAsyncThunk(
    'xero/data/getAccountCodes',
    async ({ configurationId = null, companyId = null } = {}) => {
        console.log('getAccountCodes thunk called with:', { configurationId, companyId })
        try {
            const accountCodes = await getAccountCodesForDropdown(configurationId, companyId)
            console.log('getAccountCodes thunk result:', accountCodes)
            return accountCodes
        } catch (error) {
            console.error('Error fetching account codes:', error)
            throw error
        }
    }
)

export const lineItemDefaultData = {
    description: '',
    quantity: 1,
    unit_amount: 0,
    account_code: '200', // Default to account code 200
    tax_type: 'OUTPUT', // Default to standard GST (10%)
    line_total: 0,
}

const getTodayDate = () => new Date().toISOString().split('T')[0]

const recordData = {
    Type: 'ACCREC', // Accounts Receivable (Sales Invoice)
    Contact: {
        Name: '',
        ContactID: '',
    },
    Date: getTodayDate(), // Today's date
    DueDate: '', // Optional - let Xero use customer default
    Reference: '',
    LineAmountTypes: 'Exclusive', // Tax exclusive
    LineItems: [{ ...lineItemDefaultData }],
    Status: 'DRAFT',
    // Form fields for easier handling
    customer: '',
    reference: '',
    invoice_date: getTodayDate(),
    due_date: '', // Optional - let Xero use customer default
    line_items: [{ ...lineItemDefaultData }],
    files: null,
}

// Initial table data is defined directly in the initialState object

const initialState = {
    data: [],
    tableData: {
        total: 0,
        pageIndex: 1,
        pageSize: 10,
        query: '',
        sort: {
            order: '',
            key: '',
        },
    },
    loading: false,
    recordData: recordData,
    companies: [], // Available companies
    selectedCompany: null, // Currently selected company
    xeroConfig: null, // Active Xero config for selected company
    xeroConfigs: [], // Available Xero configs for selected company
    customers: [],
    accountCodes: [],
}

const dataSlice = createSlice({
    name: 'xero/data',
    initialState: initialState,
    reducers: {
        setData: (state, { payload }) => {
            state.data = payload
        },
        setTableData: (state, { payload }) => {
            state.tableData = payload
        },
        setRecord: (state, { payload }) => {
            state.recordData = payload
        },
        resetForm: state => {
            state.recordData = recordData
        },
        setSelectedConfig: (state, { payload }) => {
            state.xeroConfig = payload
            // Clear customers and account codes when config changes so they can be reloaded
            state.customers = []
            state.accountCodes = []
        },
        setSelectedCompany: (state, { payload }) => {
            state.selectedCompany = payload
            state.xeroConfig = null // Reset config when company changes
            state.xeroConfigs = [] // Reset configs when company changes
            state.customers = [] // Reset customers when company changes
            state.accountCodes = [] // Reset account codes when company changes
        },
        addLineItem: state => {
            state.recordData.LineItems.push({ ...lineItemDefaultData })
        },
        updateLineItem: (state, { payload }) => {
            const { index, field, value } = payload
            if (state.recordData.LineItems[index]) {
                state.recordData.LineItems[index][field] = value
                // Calculate line total
                if (field === 'quantity' || field === 'unit_amount') {
                    const item = state.recordData.LineItems[index]
                    item.line_total = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_amount) || 0)
                }
            }
        },
        removeLineItem: (state, { payload }) => {
            if (state.recordData.LineItems.length > 1) {
                state.recordData.LineItems.splice(payload, 1)
            }
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getData.pending, state => {
                state.loading = true
            })
            .addCase(getData.fulfilled, (state, { payload }) => {
                state.loading = false
                state.data = payload.results
                state.tableData.total = payload.count
            })
            .addCase(getData.rejected, state => {
                state.loading = false
                state.data = []
            })
            .addCase(getRecord.pending, state => {
                state.loading = true
            })
            .addCase(getRecord.fulfilled, (state, { payload }) => {
                state.loading = false
                state.recordData = payload
            })
            .addCase(getRecord.rejected, state => {
                state.loading = false
            })
            .addCase(getCompanies.fulfilled, (state, { payload }) => {
                state.companies = payload
            })
            .addCase(getXeroConfigsForCompany.fulfilled, (state, { payload }) => {
                state.xeroConfigs = payload
                // Auto-select first config if available
                if (payload.length > 0) {
                    state.xeroConfig = payload[0]
                } else {
                    state.xeroConfig = null
                }
            })
            .addCase(getXeroConfigs.fulfilled, (state, { payload }) => {
                state.xeroConfigs = payload
                // Auto-select first config if none selected
                if (!state.xeroConfig && payload.length > 0) {
                    state.xeroConfig = payload[0]
                }
            })
            .addCase(getXeroConfig.fulfilled, (state, { payload }) => {
                state.xeroConfig = payload
            })
            .addCase(getCustomers.fulfilled, (state, { payload }) => {
                state.customers = payload
            })
            .addCase(getAccountCodes.fulfilled, (state, { payload }) => {
                state.accountCodes = payload
            })
    },
})

export const {
    setData,
    setTableData,
    setRecord,
    resetForm,
    setSelectedConfig,
    setSelectedCompany,
    addLineItem,
    updateLineItem,
    removeLineItem,
} = dataSlice.actions

export default dataSlice.reducer
